import Image from 'next/image'
import { Fade } from 'react-awesome-reveal'

import { PageMetaData } from '@/components/atomic/PageMetaData'
import { ButtonHierarchy } from '@/components/styleguide/Button'
import { VehicleCard } from '@/components/styleguide/Card/Vehicle'
import { ChatWidget } from '@/components/styleguide/ChatWidget'
import { Header } from '@/components/styleguide/Header'
import { InternalLink } from '@/components/styleguide/Link'
import { SearchResultsContainer } from '@/components/views/SearchResults/views/SearchResultsContainer'
import { constants } from '@/lib/constants'
import { ROUTES } from '@/lib/routes'
import { Size } from '@/types/system'

export default function WhyLeasePage() {
  const deal = {
    vehicle: {
      id: 10637,
      manufacturerName: 'Audi',
      manufacturer: 1,
      manufacturerWebSite: 'http://www.audi.co.uk',
      modelName: 'A4 Avant',
      model: 355,
      trimName: 'S Line',
      trim: 3354,
      derivativeName: '35 TFSI S Line 5dr S Tronic',
      fuelTypeName: 'Petrol',
      transmissionTypeName: 'Automatic',
      driveTypeName: 'Front Wheel Drive',
      stockStatus: 1,
      seats: 5,
      doors: 5,
      images: {
        profile: 'profile/audi_a4_avant_1_new.png',
        side: 'side/Audi_A4_Avant_1_new.png',
        gallery: [
          'Audi/A4 Avant/Audi_A4_Avant_1.jpg',
          'Audi/A4 Avant/Audi_A4_Avant_2.jpg',
          'Audi/A4 Avant/Audi_A4_Avant_3.jpg',
          'Audi/A4 Avant/Audi_A4_Avant_4.jpg',
          'Audi/A4 Avant/Audi_A4_Avant_5.jpg',
          'Audi/A4 Avant/Audi_A4_Avant_6.jpg',
          'Audi/A4 Avant/Audi_A4_Avant_7.jpg',
        ],
      },
      vehicleType: 1,
    },
    vehiclePrice: {
      manufacturerName: 'Audi',
      modelName: 'A4 Avant',
      derivativeName: '35 TFSI S Line 5dr S Tronic',
      id: 134403080,
      vehicleId: 10637,
      rating: 86,
      initialPaymentInMoney: 0,
      initialPaymentInMonths: 9,
      documentFee: 0,
      leaseType: 2,
      term: 48,
      mileage: 10000,
      monthlyPayment: 245.23,
      monthlyPaymentAverage: null,
      stockStatus: 1,
    },
  }

  return (
    <>
      <PageMetaData
        title="Why Lease A Car? - Car Leasing Made Simple at LeaseLoco"
        description="If you're wondering why you should lease a car, look no further, we'll give you 7 great reasons. Car Leasing Made Simple at LeaseLoco."
        pageUrl={ROUTES.whyLeasePage.url}
      />
      <ChatWidget />
      <Header />
      <div className="white-base-layer">
        <div className="container container--body container--why-lease">
          <div className="row">
            <div className="col-12 col-lg-10 offset-lg-1 text-left">
              <Fade triggerOnce>
                <div className="why-lease__container why-lease__container--white pt-0">
                  <h1 className="secondary-header">Why lease?</h1>
                  <p className="text-semibold">
                    The #1 reason to choose{' '}
                    <InternalLink
                      href={ROUTES.homePage.url}
                      size={Size.Large}
                      hierarchy={ButtonHierarchy.Borderless}>
                      leasing
                    </InternalLink>{' '}
                    over buying is COST.
                  </p>
                  <p className="mb-4">
                    Take this example of this Audi A4 Avant we have a deal<sup>*</sup> on:
                  </p>
                  <SearchResultsContainer>
                    <VehicleCard deal={deal} />
                  </SearchResultsContainer>
                  <p className="mt-4">A few things to note:</p>
                  <ol>
                    <li>The list price of the car is £36,995.00</li>
                    <li>The trade in price for this car is around £13,000.</li>
                    <li>
                      This all means there is around £24,000 of depreciation. And it doesn&apos;t
                      even take into account financing costs.
                    </li>
                  </ol>
                  <p>
                    Most people when they buy a new car take out a PCP loan (Personal Contract
                    Purchase). So let&apos;s look at the cost of this for the A4...
                  </p>
                  <p>
                    <a
                      className="link"
                      href="https://www.audi.co.uk/finance-and-offers/pcp-deals.html"
                      target="_blank"
                      rel="noopener noreferrer">
                      Audi are offering a PCP deal
                    </a>
                    <sup>*</sup> on their website for the exact same A4 Avant model also on a 4 year
                    term with 10,000 miles per annum.
                  </p>
                  <div className="table-container my-4">
                    <div className="table-responsive">
                      <table className="table table-borderless table-sm">
                        <caption>PCP figures from Audi</caption>
                        <tbody>
                          <tr>
                            <td>47 monthly payments of</td>
                            <td className="background-grey border-top-right">£389.00</td>
                          </tr>
                          <tr>
                            <td>Intial payment</td>
                            <td className="background-grey">£5,926.66</td>
                          </tr>
                          <tr>
                            <td>Total over term</td>
                            <td className="background-grey border-bottom-right">£24,209.66</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-borderless table-sm">
                        <caption>Lease figures from LeaseLoco</caption>
                        <tbody>
                          <tr>
                            <td>47 monthly payments of</td>
                            <td className="background-grey border-top-right">£294.28</td>
                          </tr>
                          <tr>
                            <td>Intial payment</td>
                            <td className="background-grey">£2,648.48</td>
                          </tr>
                          <tr>
                            <td>Total over term</td>
                            <td className="background-grey border-bottom-right">£16,479.64</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <p>
                    The lease deal on LeaseLoco would save you{' '}
                    <span className="pink">£7,730.02</span> over the course of the term compared to
                    the PCP deal on offer from Audi. That&apos;s almost £2,000 per year!!
                  </p>
                  <p>And that Hombre, is the main reason leasing is better than buying!</p>
                </div>
              </Fade>
            </div>
          </div>
        </div>
      </div>
      <div className="alternate-base-layer">
        <div className="container-fluid container--body container--why-lease">
          <div className="row">
            <div className="col-12 col-lg-5 order-lg-2 text-left">
              <Fade
                delay={400}
                triggerOnce>
                <div className="why-lease__container pt-0">
                  <p className="text-large">ARRIBA!</p>
                  <p>So any other good reasons?</p>
                  <p>Of course, Amigos!</p>
                  <p>
                    <span>New cars.</span> All{' '}
                    <InternalLink
                      className="link"
                      href={ROUTES.homePage.url}
                      hierarchy={ButtonHierarchy.Borderless}
                      size={Size.Large}>
                      cars
                    </InternalLink>{' '}
                    and{' '}
                    <InternalLink
                      className="link"
                      href={ROUTES.vanHomePage.url}
                      hierarchy={ButtonHierarchy.Borderless}
                      size={Size.Large}>
                      vans
                    </InternalLink>{' '}
                    leased through LeaseLoco are brand new. Some may be pre-registered but our
                    leasing partner would confirm that when you make an enquiry with them.
                  </p>
                  <p>
                    <span>No road tax.</span> All cars leased through LeaseLoco include road tax in
                    the monthly payment. This can save you hundreds of £££ over the term.
                  </p>
                  <p>
                    <span>Flexibility.</span> Funders of lease cars are becoming more and more aware
                    for lease contracts to be flexible. This means you can often terminate the lease
                    early without being committed to the remaining lease payments; you will have
                    early termination penalties but these can often be significantly less than the
                    remaining lease payments (around 55% in some cases but this will vary on a case
                    to case basis and you should always contact your funder to clarify the exact
                    figures).
                  </p>
                  <p>
                    <span>Breakdown cover.</span> More and more manufacturers provide free breakdown
                    cover for the warranty period of the car, meaning even more piece of mind for
                    you.
                  </p>
                  <p>
                    <span>Never sell your car again.</span> You don&apos;t own the car so you
                    don&apos;t sell it. You simply hand it back. So no hassle and no tyre kickers at
                    the end of your term to worry about.
                  </p>
                  <p>
                    <span>No depreciation worries.</span> Your payments are fixed so even if the
                    government massively increases tax on diesels in the middle of your lease, you
                    don&apos;t have to worry about it.
                  </p>
                </div>
              </Fade>
            </div>
            <div className="col-12 col-lg-3 offset-lg-2 order-lg-1">
              <div className="why-lease__image-container">
                <Image
                  src={`${constants.imageHost.assets}/mascot/full-body-3.png`}
                  alt="Luis Loco"
                  height="473"
                  width="320"
                />
              </div>
            </div>
            <div className="col-12 order-3">
              <p className="text-small mt-5">
                <sup>*</sup> Both lease and PCP deals were available on 13 February 2020 and are
                most likely not going to be around while you&apos;re reading this but the point
                still stands.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
